import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Listing } from 'listings/interfaces/listing.interface';
import { ListingValidationService } from 'listings/services/listing-validation.service';
import { ListingsService } from 'listings/services/listings.service';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { BaseModal } from 'shared/abstract/base-modal';
import { LoaderComponent } from 'shared/components/loader/loader.component';
import { ListingPartPhase, PartStatusInfo } from 'shared/interfaces/listing-parts.interface';
import { EventBusService, PermissionsService, ToastService } from 'shared/services';
import { ListingPartsStatusService } from 'shared/services/listing-parts-status.service';
import { VisualsListingsService } from 'visuals/services/visual-listings.service';

enum ListingPart {
    CONTENT = 'CONTENT',
    VISUALS = 'VISUALS',
}

enum PartAction {
    NONE = 'NONE',
    SUBMIT_FOR_PUBLISHING = 'SUBMIT_FOR_PUBLISHING',
    SUBMIT_FOR_REVIEW = 'SUBMIT_FOR_REVIEW',
    RETURN_TO_EDITOR = 'RETURN_TO_EDITOR',
    CREATE_DRAFT = 'CREATE_DRAFT',
    DISCARD = 'DISCARD',
}

interface PartOption {
    action: PartAction;
    title: string;
    description: string;
    icon: string;
}

interface PartSection {
    part: ListingPart;
    title: string;
    icon: string;
    status: PartStatusInfo | null;
    phase: ListingPartPhase;
    options: PartOption[];
    errorCount: number;
    selectedAction: PartAction;
}

interface PartResult {
    part: ListingPart;
    action: PartAction;
    errorMessage: string | null;
}

const PART_TITLES: { [key in ListingPart]: string } = {
    [ListingPart.CONTENT]: 'listing.validationIssues.categoryContent',
    [ListingPart.VISUALS]: 'listing.validationIssues.categoryVisuals',
};

const PART_ICONS: { [key in ListingPart]: string } = {
    [ListingPart.CONTENT]: 'far fa-bars',
    [ListingPart.VISUALS]: 'far fa-image',
};

const SAVE_ONLY_OPTION: PartOption = {
    action: PartAction.NONE,
    title: 'listing.save.saveOptions.saveOnly',
    description: 'listing.save.descriptionSaveOnlyLabel',
    icon: 'far fa-save',
};
const LEAVE_UNCHANGED_LIVE_OPTION: PartOption = {
    action: PartAction.NONE,
    title: 'listing.save.saveOptions.noChange',
    description: 'listing.save.descriptionNoChangeLiveLabel',
    icon: 'far fa-check',
};
const LEAVE_UNCHANGED_PENDING_OPTION: PartOption = {
    action: PartAction.NONE,
    title: 'listing.save.saveOptions.noChange',
    description: 'listing.save.descriptionNoChangePendingLabel',
    icon: 'far fa-check',
};
const SUBMIT_FOR_PUBLISHING_OPTION: PartOption = {
    action: PartAction.SUBMIT_FOR_PUBLISHING,
    title: 'listing.save.saveOptions.submitForPublishing',
    description: 'listing.save.descriptionPublishLabel',
    icon: 'far fa-upload',
};
const SUBMIT_FOR_REVIEW_OPTION: PartOption = {
    action: PartAction.SUBMIT_FOR_REVIEW,
    title: 'listing.save.saveOptions.submitForApproval',
    description: 'listing.save.descriptionSendForApprovalLabel',
    icon: 'far fa-user-check',
};
const RETURN_TO_EDITOR_OPTION: PartOption = {
    action: PartAction.RETURN_TO_EDITOR,
    title: 'listing.save.saveOptions.returnToEditor',
    description: 'listing.save.descriptionReturnToEditorLabel',
    icon: 'far fa-rotate-left',
};
const CREATE_DRAFT_OPTION: PartOption = {
    action: PartAction.CREATE_DRAFT,
    title: 'listing.createDraft',
    description: 'listing.save.descriptionCreateDraftLabel',
    icon: 'far fa-plus',
};
const DISCARD_OPTION: PartOption = {
    action: PartAction.DISCARD,
    title: 'listing.save.saveOptions.discard',
    description: 'listing.save.descriptionDiscardLabel',
    icon: 'far fa-xmark',
};

const ACTION_RESULTS: { [key in PartAction]: string } = {
    [PartAction.NONE]: '',
    [PartAction.SUBMIT_FOR_PUBLISHING]: 'listing.save.partResult.submittedForPublishing',
    [PartAction.SUBMIT_FOR_REVIEW]: 'listing.save.partResult.submittedForReview',
    [PartAction.RETURN_TO_EDITOR]: 'listing.save.partResult.returnedToEditor',
    [PartAction.CREATE_DRAFT]: 'listing.save.partResult.draftCreated',
    [PartAction.DISCARD]: 'listing.save.partResult.draftDiscarded',
};

const SUBMIT_ACTIONS = [PartAction.SUBMIT_FOR_PUBLISHING, PartAction.SUBMIT_FOR_REVIEW];

function isBlockedByErrors(action: PartAction, errorCount: number): boolean {
    return SUBMIT_ACTIONS.includes(action) && errorCount > 0;
}

@Component({
    selector: 'app-saving-options-modal',
    templateUrl: './saving-options-modal.component.html',
    styleUrl: './saving-options-modal.component.scss',
    imports: [FormsModule, LoaderComponent, TranslateModule],
})
export class SavingOptionsModalComponent extends BaseModal {
    data!: { listing: Listing };

    protected readonly ListingPartPhase = ListingPartPhase;
    protected readonly PartAction = PartAction;

    protected selectedActions = signal<{ [key in ListingPart]: PartAction }>({
        [ListingPart.CONTENT]: PartAction.NONE,
        [ListingPart.VISUALS]: PartAction.NONE,
    });
    protected rejectReasons: { [key in ListingPart]: string } = { [ListingPart.CONTENT]: '', [ListingPart.VISUALS]: '' };
    protected isRunning = signal(false);
    protected results = signal<{ [key in ListingPart]?: PartResult }>({});
    protected hasFailure = computed(() => Object.values(this.results()).some((result) => result.errorMessage !== null));

    private isApprover = toSignal(this.permissionsService.userHasAccess('listings.saveOptions.submitForPublishing'), {
        initialValue: false,
    });

    protected sections = computed<PartSection[]>(() => {
        const sections = [
            this.buildSection(
                ListingPart.CONTENT,
                this.listingPartsStatusService.contentPhase(),
                this.listingPartsStatusService.contentStatusInfo(),
                this.listingValidationService.contentErrors().length,
            ),
        ];
        const visualsPhase = this.listingPartsStatusService.visualsPhase();
        if (visualsPhase !== ListingPartPhase.HIDDEN) {
            sections.push(
                this.buildSection(
                    ListingPart.VISUALS,
                    visualsPhase,
                    this.listingPartsStatusService.visualsStatusInfo(),
                    this.listingValidationService.visualErrors().length,
                ),
            );
        }
        return sections;
    });

    protected canConfirm = computed(() => this.sections().some((section) => section.selectedAction !== PartAction.NONE));

    constructor(
        private listingsService: ListingsService,
        private visualsListingsService: VisualsListingsService,
        private listingPartsStatusService: ListingPartsStatusService,
        private listingValidationService: ListingValidationService,
        private permissionsService: PermissionsService,
        private eventBusService: EventBusService,
        private toastService: ToastService,
        private translate: TranslateService,
        private destroyRef: DestroyRef,
    ) {
        super();
    }

    protected isOptionBlocked(section: PartSection, option: PartOption): boolean {
        return isBlockedByErrors(option.action, section.errorCount);
    }

    protected selectAction(section: PartSection, option: PartOption): void {
        if (this.isRunning() || this.isOptionBlocked(section, option)) {
            return;
        }
        this.selectedActions.update((selectedActions) => ({ ...selectedActions, [section.part]: option.action }));
    }

    protected confirm(): void {
        const runs = this.sections()
            .filter((section) => section.selectedAction !== PartAction.NONE)
            .map((section) => this.runPartAction(section.part, section.selectedAction));
        if (!runs.length || this.isRunning()) {
            return;
        }

        this.eventBusService.dispatch('confirmedListingActionModal');
        this.isRunning.set(true);
        forkJoin(runs)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((results) => {
                this.results.set(Object.fromEntries(results.map((result) => [result.part, result])));
                this.isRunning.set(false);
                if (!this.hasFailure()) {
                    results.forEach((result) =>
                        this.toastService.showToast(ACTION_RESULTS[result.action], 'success', {
                            part: this.translate.instant(PART_TITLES[result.part]),
                        }),
                    );
                    window.location.reload();
                }
            });
    }

    protected closeAndReload(): void {
        this.closeModal();
        window.location.reload();
    }

    closeModal(): void {
        this.closeModalInstance(false);
    }

    private buildSection(part: ListingPart, phase: ListingPartPhase, status: PartStatusInfo | null, errorCount: number): PartSection {
        const options = this.buildOptions(part, phase);
        const selectedAction = this.selectedActions()[part];
        const isSelectable = options.some((option) => option.action === selectedAction && !isBlockedByErrors(option.action, errorCount));
        return {
            part,
            title: PART_TITLES[part],
            icon: PART_ICONS[part],
            status,
            phase,
            options,
            errorCount,
            selectedAction: isSelectable ? selectedAction : PartAction.NONE,
        };
    }

    private buildOptions(part: ListingPart, phase: ListingPartPhase): PartOption[] {
        const isApprover = this.isApprover();
        switch (phase) {
            case ListingPartPhase.DRAFT:
                return [
                    SAVE_ONLY_OPTION,
                    isApprover ? SUBMIT_FOR_PUBLISHING_OPTION : SUBMIT_FOR_REVIEW_OPTION,
                    ...(this.canDiscard(part) ? [DISCARD_OPTION] : []),
                ];
            case ListingPartPhase.PENDING:
                return isApprover
                    ? [LEAVE_UNCHANGED_PENDING_OPTION, SUBMIT_FOR_PUBLISHING_OPTION, RETURN_TO_EDITOR_OPTION]
                    : [LEAVE_UNCHANGED_PENDING_OPTION];
            case ListingPartPhase.LIVE:
                return [LEAVE_UNCHANGED_LIVE_OPTION, CREATE_DRAFT_OPTION];
            default:
                return [];
        }
    }

    private canDiscard(part: ListingPart): boolean {
        return part === ListingPart.VISUALS || this.data.listing.maxVersion > 1;
    }

    private runPartAction(part: ListingPart, action: PartAction): Observable<PartResult> {
        const listingId = this.data.listing.id;
        const rejectReason = this.rejectReasons[part].trim();
        const request =
            part === ListingPart.CONTENT
                ? this.contentRequest(action, listingId, rejectReason)
                : this.visualsRequest(action, listingId, rejectReason);

        return request.pipe(
            map(() => ({ part, action, errorMessage: null })),
            catchError((error: HttpErrorResponse) => of({ part, action, errorMessage: this.getErrorMessage(error) })),
        );
    }

    private contentRequest(action: PartAction, listingId: string, rejectReason: string): Observable<unknown> {
        switch (action) {
            case PartAction.SUBMIT_FOR_PUBLISHING:
            case PartAction.SUBMIT_FOR_REVIEW:
                return this.listingsService.submitListing(listingId);
            case PartAction.RETURN_TO_EDITOR:
                return this.listingsService.rejectListing(listingId, rejectReason);
            case PartAction.CREATE_DRAFT:
                return this.listingsService.createDraftListing(listingId);
            case PartAction.DISCARD:
                return this.listingsService.dropDraftListing(listingId);
            default:
                return of(null);
        }
    }

    private visualsRequest(action: PartAction, listingId: string, rejectReason: string): Observable<unknown> {
        switch (action) {
            case PartAction.SUBMIT_FOR_PUBLISHING:
                return this.visualsListingsService.submitListing(listingId);
            case PartAction.SUBMIT_FOR_REVIEW:
                return this.visualsListingsService.submitListingForReview(listingId);
            case PartAction.RETURN_TO_EDITOR:
                return this.visualsListingsService.returnListingToEditor(listingId, rejectReason);
            case PartAction.CREATE_DRAFT:
                return this.visualsListingsService.createDraftListing(listingId);
            case PartAction.DISCARD:
                return this.visualsListingsService.dropDraftListing(listingId);
            default:
                return of(null);
        }
    }

    private getErrorMessage(error: HttpErrorResponse): string {
        if (typeof error.error === 'string') {
            return error.error;
        }
        return error.error?.Error ?? error.message;
    }
}
